import { useState } from "react";
import { Minus, Plus, Trash2, Loader2, FileText, PackageOpen } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useQuote } from "@/context/QuoteContext";

const EMPTY = { nome: "", azienda: "", email: "", telefono: "", messaggio: "" };

const QuoteDialog = () => {
  const { isOpen, closeDialog, items, removeItem, updateQuantity, clearItems } = useQuote();
  const [form, setForm] = useState(EMPTY);
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.telefono.trim() || !form.messaggio.trim()) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    if (form.telefono.trim().length < 3) {
      toast.error("Inserisci un numero di telefono valido.");
      return;
    }
    if (!privacy) {
      toast.error("Devi accettare il trattamento dei dati (GDPR).");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/quote-request", {
        ...form,
        products: items.map((p) => ({
          code: p.code,
          name: p.name,
          category: p.category,
          quantity: p.quantity,
        })),
      });
      if (data.success) {
        toast.success(
          "Richiesta di preventivo inviata! Il nostro ufficio tecnico ti contatterà entro 24 ore."
        );
        setForm(EMPTY);
        setPrivacy(false);
        clearItems();
        closeDialog();
      }
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o scrivici a magitimpianti@libero.it");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => (o ? null : closeDialog())}>
      <DialogContent
        data-testid="quote-dialog"
        className="w-[calc(100vw-1.5rem)] max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden grid-cols-1 rounded-lg bg-white p-5 text-slate-900 sm:p-6"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-brand" />
            Richiesta di Preventivo
          </DialogTitle>
          <DialogDescription>
            Seleziona i prodotti e inviaci i tuoi dati: nessun pagamento online, ti ricontattiamo
            con un preventivo personalizzato.
          </DialogDescription>
        </DialogHeader>

        {/* Selected products */}
        <div className="mb-2">
          <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
            Prodotti selezionati ({items.length})
          </h4>
          {items.length === 0 ? (
            <div
              data-testid="quote-empty-state"
              className="flex items-center gap-3 text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-200 rounded-lg p-4"
            >
              <PackageOpen className="w-5 h-5 shrink-0" />
              Nessun prodotto selezionato. Puoi comunque inviare una richiesta generica compilando il
              form, oppure aggiungi prodotti dallo SHOP.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="quote-items-list">
              {items.map((p) => (
                <li
                  key={p.id}
                  data-testid={`quote-item-${p.id}`}
                  className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg p-2.5"
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-12 h-12 rounded object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[11px] text-brand">{p.code}</div>
                    <div className="text-sm font-medium text-slate-800 truncate">{p.name}</div>
                  </div>
                  <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full">
                    <button
                      type="button"
                      data-testid={`quote-qty-minus-${p.id}`}
                      onClick={() => updateQuantity(p.id, -1)}
                      className="p-1.5 text-slate-500 hover:text-slate-900"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-sm font-semibold w-5 text-center">{p.quantity}</span>
                    <button
                      type="button"
                      data-testid={`quote-qty-plus-${p.id}`}
                      onClick={() => updateQuantity(p.id, 1)}
                      className="p-1.5 text-slate-500 hover:text-slate-900"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    type="button"
                    data-testid={`quote-remove-${p.id}`}
                    onClick={() => removeItem(p.id)}
                    className="p-2 text-slate-400 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2" data-testid="quote-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="q-nome">Nome e Cognome *</Label>
              <Input
                id="q-nome"
                data-testid="quote-input-nome"
                value={form.nome}
                onChange={setField("nome")}
                placeholder="Mario Rossi"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="q-azienda">Azienda / Ente</Label>
              <Input
                id="q-azienda"
                data-testid="quote-input-azienda"
                value={form.azienda}
                onChange={setField("azienda")}
                placeholder="(facoltativo)"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="q-email">Email *</Label>
              <Input
                id="q-email"
                type="email"
                data-testid="quote-input-email"
                value={form.email}
                onChange={setField("email")}
                placeholder="nome@email.it"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="q-telefono">Telefono *</Label>
              <Input
                id="q-telefono"
                type="tel"
                data-testid="quote-input-telefono"
                value={form.telefono}
                onChange={setField("telefono")}
                placeholder="+39 ..."
                className="mt-1.5"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="q-messaggio">Messaggio / Specifiche di Progetto *</Label>
            <Textarea
              id="q-messaggio"
              data-testid="quote-input-messaggio"
              value={form.messaggio}
              onChange={setField("messaggio")}
              placeholder="Descrivi la tua esigenza, quantità, tempistiche..."
              rows={4}
              className="mt-1.5"
            />
          </div>

          <div className="flex items-start gap-2.5">
            <Checkbox
              id="q-privacy"
              data-testid="quote-input-privacy"
              checked={privacy}
              onCheckedChange={(v) => setPrivacy(!!v)}
              className="mt-0.5"
            />
            <Label htmlFor="q-privacy" className="text-xs text-slate-500 font-normal leading-relaxed">
              Ho letto la{" "}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="quote-privacy-link" className="text-brand underline">Privacy Policy</a>{" "}
              e la{" "}
              <a href="/cookie-policy" target="_blank" rel="noopener noreferrer" data-testid="quote-cookie-link" className="text-brand underline">Cookie Policy</a>{" "}
              e accetto il trattamento dei dati personali ai sensi del Regolamento UE 2016/679 (GDPR).
            </Label>
          </div>

          <Button
            type="submit"
            data-testid="quote-submit-button"
            disabled={submitting}
            className="w-full bg-brand hover:bg-brand-hover text-white font-semibold rounded-full h-12 text-base"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Invio in corso...
              </>
            ) : (
              "Invia Richiesta Preventivo"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteDialog;
